import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/16/solid';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'hotel/navigation';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';

import { PrimaryButtonLink } from 'components/Button';
import {
  InHouseCard,
  ListCard,
  ListCardItem,
  UpcomingCard,
} from 'components/Card';
import DynamicCityGrid from 'components/DynamicCityGrid';
import ErrorMessage from 'components/ErrorMessage';
import Loading from 'components/Loading';
import MainHotelSearchForm, {
  FormValues,
} from 'components/MainHotelSearchForm';
import { SectionHeader } from 'components/SectionHeader';
import TravelerSelfBookingGuard from 'components/TravelerSelfBookingGuard';
import { useDashboardReportQuery } from 'generated/graphql';
import { useUserRole } from 'hooks/useUserRole';
import { setBookingType, setBookingDetails } from 'store/bookingSlice';
import { reset as resetGroupDetails } from 'store/groupDetailsSlice';
import { useAppSelector } from 'store/store';
import { generateFilterLink } from 'utilities/generateFilterLink';
import getCurrentWeekRange from 'utilities/getCurrentWeekRange';

import HeaderImage from './HeaderImage';

export default function Dashboard() {
  const { data, error, loading } = useDashboardReportQuery();
  const summary = data?.dashboardReport?.summary;
  const cities = data?.dashboardReport?.cities;
  const checkins = data?.dashboardReport?.checkins;

  const { canViewFullRoster } = useUserRole();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Check if client is inactive
  const client = useAppSelector((state) => state.user.profile?.client);
  const isClientInactive = client?.isInactive ?? false;

  const { startOfWeek, endOfWeek } = getCurrentWeekRange();

  const [searchParams] = useSearchParams();

  type MainFormValues = FormValues & {
    bookingType: 'group' | 'instant';
  };
  const getFormInitialValuesFromURL = (): MainFormValues | null => {
    const {
      latitude,
      longitude,
      checkInDate,
      checkOutDate,
      roomQuantity,
      guestQuantity,
      description,
    } = Object.fromEntries(searchParams.entries());

    const bookingType =
      searchParams.get('bookingType') === 'group' ? 'group' : 'instant';

    if (
      latitude &&
      longitude &&
      checkInDate &&
      checkOutDate &&
      roomQuantity &&
      guestQuantity
    ) {
      return {
        city: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          description: description || undefined,
        },
        checkInDate,
        checkOutDate,
        roomQuantity,
        guestQuantity,
        bookingType,
      };
    }
    return null;
  };

  const handleFormSubmit = (values: FormValues) => {
    try {
      const params: Record<string, string> = {
        latitude: (values.city?.latitude || 0).toString(),
        longitude: (values.city?.longitude || 0).toString(),
        checkInDate: values.checkInDate,
        checkOutDate: values.checkOutDate,
        roomQuantity: Number(values.roomQuantity || 1).toString(),
        guestQuantity: Number(values.guestQuantity || 1).toString(),
        radius: '1',
      };

      if (values.city?.description) {
        params.description = values.city.description;
      }

      dispatch(setBookingType(values.bookingType));

      // Create search params once
      const searchParams = new URLSearchParams(params);

      // If it's a group booking, store the details in Redux
      if (values.bookingType === 'group') {
        // Reset group details (including room configurations) when starting a new search
        dispatch(resetGroupDetails());

        dispatch(
          setBookingDetails({
            latitude: params.latitude,
            longitude: params.longitude,
            checkInDate: params.checkInDate,
            checkOutDate: params.checkOutDate,
            description: params.description,
            guestQuantity: '1', // Default to 1 for group flow (not used in room-centric approach)
            roomQuantity: params.roomQuantity, // Use actual room quantity for groups
          }),
        );
      }

      // Navigate to appropriate page with search params
      const targetPath =
        values.bookingType === 'instant' ? '/search' : '/groups';
      navigate(`${targetPath}/?${searchParams.toString()}`);
    } catch {
      // Error submitting form - handled silently
    }
  };

  return (
    <>
      <TravelerSelfBookingGuard>
        <div className="w-full overflow-hidden">
          <HeaderImage>
            <MainHotelSearchForm
              onSubmit={(values) => handleFormSubmit(values as FormValues)}
              initialValues={getFormInitialValuesFromURL()}
              disabled={isClientInactive}
            />
          </HeaderImage>
        </div>
      </TravelerSelfBookingGuard>

      <div className="mb-40">
        <div className="mx-auto max-w-7xl p-6 md:p-8 xl:p-0">
          <Helmet>
            <title>Dashboard</title>
          </Helmet>

          {error && (
            <div className="mb-5">
              <ErrorMessage errorMessage={error.message} />
            </div>
          )}

          {canViewFullRoster && (
            <>
              <div className="my-4 lg:my-8 xl:my-14">
                <SectionHeader headline="Live Roster Insights">
                  <PrimaryButtonLink to="/roster" text="View Live Roster" />
                </SectionHeader>
              </div>

              {(loading || !summary) && <Loading />}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {summary && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="col-span-2 sm:col-span-1">
                      <InHouseCard
                        count={summary.inHouse}
                        link={generateFilterLink('roster', {
                          status: ['IN_HOUSE'],
                        })}
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <UpcomingCard
                        count={summary.upcoming}
                        link={generateFilterLink('roster', {
                          status: ['UPCOMING'],
                        })}
                      />
                    </div>
                    {checkins && (
                      <div className="col-span-2 col-start-1">
                        <ListCard
                          title="Insights"
                          titleColor="text-primaryGray"
                          footerLink={generateFilterLink('roster', {
                            duplicateApproved: true,
                            status: ['IN_HOUSE', 'UPCOMING'],
                          })}
                        >
                          <ListCardItem
                            description="Approved Duplicate Bookings"
                            number={checkins.duplicateApprovals}
                            icon={CheckCircleIcon}
                            iconBgColor="bg-yellow-400"
                          />
                        </ListCard>
                      </div>
                    )}
                  </div>
                )}

                {checkins && (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
                    <ListCard
                      title="Daily Activities"
                      titleColor="text-primaryGray"
                    >
                      <ListCardItem
                        description="Daily Check Ins"
                        number={checkins.checkIns}
                        link={generateFilterLink('roster', {
                          checkInDate: {
                            start: new Date().toISOString().split('T')[0],
                          },
                        })}
                        icon={ArrowRightIcon}
                        iconBgColor="bg-primaryBlue"
                      />
                      <ListCardItem
                        description="Daily Check Outs"
                        number={checkins.checkOuts}
                        link={generateFilterLink('roster', {
                          checkOutDate: {
                            start: new Date().toISOString().split('T')[0],
                          },
                        })}
                        icon={ArrowLeftIcon}
                        iconBgColor="bg-primaryBlue"
                      />
                    </ListCard>
                    <ListCard
                      title="Weekly Activities"
                      titleColor="text-primaryGray"
                    >
                      <ListCardItem
                        description="Weekly Check Ins"
                        number={checkins.checkInsWeekly}
                        link={generateFilterLink('roster', {
                          checkInDate: {
                            start: startOfWeek,
                            end: endOfWeek,
                          },
                        })}
                        icon={ArrowRightIcon}
                        iconBgColor="bg-primaryBlue"
                      />
                    </ListCard>
                  </div>
                )}
              </div>
            </>
          )}

          {canViewFullRoster && (
            <>
              <SectionHeader headline="Lodging Overview" className="mt-16" />

              {(loading || !cities) && <Loading />}

              {!loading && (!cities || cities.length < 1) && (
                <p>No roster information found.</p>
              )}

              {cities && cities.length > 0 && (
                <DynamicCityGrid cities={cities} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
